import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ParentalSection = 'liveTV' | 'movies' | 'series' | 'sports' | 'anime' | 'kids';

interface BlockedSections {
  liveTV: boolean;
  movies: boolean;
  series: boolean;
  sports: boolean;
  anime: boolean;
  kids: boolean;
}

export interface ParentalControlsState {
  enabled: boolean;
  kidsMode: boolean;
  /** btoa-encoded password; null when no password set. */
  passwordHash: string | null;
  blockedSections: BlockedSections;
  blockedLiveCategoryIds: string[];
  blockedMovieCategoryIds: string[];
  blockedSeriesCategoryIds: string[];
  blockedLiveChannelIds: string[];
}

interface ParentalControlsContextType {
  state: ParentalControlsState;
  /** True when enabled=true */
  isEnabled: boolean;
  /** True when kidsMode=true (independent of parental.enabled) */
  isKidsMode: boolean;
  /** Granular lock screen — parental enabled, kids off, section blocked */
  isBlocked: (section: ParentalSection) => boolean;
  /** Navbar visibility — kids mode hides non-kids sections without lock UI */
  isNavVisible: (section: ParentalSection) => boolean;
  hasPassword: () => boolean;
  verifyPassword: (input: string) => boolean;
  setPassword: (newPass: string) => void;
  changePassword: (oldPass: string, newPass: string) => boolean;
  setEnabled: (value: boolean) => void;
  setKidsMode: (value: boolean) => void;
  setSectionBlocked: (section: ParentalSection, value: boolean) => void;
  setBlockedLiveCategories: (ids: string[]) => void;
  setBlockedMovieCategories: (ids: string[]) => void;
  setBlockedSeriesCategories: (ids: string[]) => void;
  setLiveChannelBlocked: (id: string, value: boolean) => void;
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

const DEFAULT_STATE: ParentalControlsState = {
  enabled: false,
  kidsMode: false,
  passwordHash: null,
  blockedSections: {
    liveTV: false,
    movies: false,
    series: false,
    sports: false,
    anime: false,
    kids: false,
  },
  blockedLiveCategoryIds: [],
  blockedMovieCategoryIds: [],
  blockedSeriesCategoryIds: [],
  blockedLiveChannelIds: [],
};

const STORAGE_KEY = 'ott_parental_controls_v1';

function loadState(): ParentalControlsState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_STATE;
    const parsed = JSON.parse(raw) as Partial<ParentalControlsState>;
    return {
      ...DEFAULT_STATE,
      ...parsed,
      blockedSections: { ...DEFAULT_STATE.blockedSections, ...(parsed.blockedSections ?? {}) },
      blockedLiveCategoryIds: Array.isArray(parsed.blockedLiveCategoryIds) ? parsed.blockedLiveCategoryIds : [],
      blockedMovieCategoryIds: Array.isArray(parsed.blockedMovieCategoryIds) ? parsed.blockedMovieCategoryIds : [],
      blockedSeriesCategoryIds: Array.isArray(parsed.blockedSeriesCategoryIds) ? parsed.blockedSeriesCategoryIds : [],
      blockedLiveChannelIds: Array.isArray(parsed.blockedLiveChannelIds) ? parsed.blockedLiveChannelIds : [],
    };
  } catch {
    return DEFAULT_STATE;
  }
}

function persistState(s: ParentalControlsState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    // ignore quota errors
  }
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const ParentalControlsContext = createContext<ParentalControlsContextType | undefined>(undefined);

export const ParentalControlsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<ParentalControlsState>(loadState);

  // Persist on every state change
  useEffect(() => {
    persistState(state);
  }, [state]);

  const update = useCallback((patch: Partial<ParentalControlsState> | ((prev: ParentalControlsState) => ParentalControlsState)) => {
    setState((prev) => {
      const next = typeof patch === 'function' ? patch(prev) : { ...prev, ...patch };
      return next;
    });
  }, []);

  // ---- computed ----------------------------------------------------------

  const isEnabled = state.enabled;
  const isKidsMode = state.kidsMode;

  const isBlocked = useCallback(
    (section: ParentalSection): boolean => {
      if (state.kidsMode) return false;
      if (!state.enabled) return false;
      return state.blockedSections[section] ?? false;
    },
    [state]
  );

  const isNavVisible = useCallback(
    (section: ParentalSection): boolean => {
      if (state.kidsMode) {
        return section === 'kids' || section === 'liveTV';
      }
      if (!state.enabled) return true;
      return !(state.blockedSections[section] ?? false);
    },
    [state]
  );

  // ---- password ----------------------------------------------------------

  const hasPassword = useCallback((): boolean => state.passwordHash !== null, [state.passwordHash]);

  const verifyPassword = useCallback(
    (input: string): boolean => {
      if (state.passwordHash === null) return true; // no password set — always ok
      return btoa(input) === state.passwordHash;
    },
    [state.passwordHash]
  );

  const setPassword = useCallback((newPass: string) => {
    update({ passwordHash: btoa(newPass) });
  }, [update]);

  const changePassword = useCallback(
    (oldPass: string, newPass: string): boolean => {
      if (state.passwordHash !== null && btoa(oldPass) !== state.passwordHash) return false;
      update({ passwordHash: btoa(newPass) });
      return true;
    },
    [state.passwordHash, update]
  );

  // ---- mutations ---------------------------------------------------------

  const setEnabled = useCallback((value: boolean) => update({ enabled: value }), [update]);
  const setKidsMode = useCallback((value: boolean) => update({ kidsMode: value }), [update]);

  const setSectionBlocked = useCallback(
    (section: ParentalSection, value: boolean) => {
      update((prev) => ({
        ...prev,
        blockedSections: { ...prev.blockedSections, [section]: value },
      }));
    },
    [update]
  );

  const setBlockedLiveCategories = useCallback(
    (ids: string[]) => update({ blockedLiveCategoryIds: ids }),
    [update]
  );
  const setBlockedMovieCategories = useCallback(
    (ids: string[]) => update({ blockedMovieCategoryIds: ids }),
    [update]
  );
  const setBlockedSeriesCategories = useCallback(
    (ids: string[]) => update({ blockedSeriesCategoryIds: ids }),
    [update]
  );

  const setLiveChannelBlocked = useCallback(
    (id: string, value: boolean) => {
      update((prev) => ({
        ...prev,
        blockedLiveChannelIds: value
          ? Array.from(new Set([...prev.blockedLiveChannelIds, id]))
          : prev.blockedLiveChannelIds.filter((item) => item !== id),
      }));
    },
    [update]
  );

  const value = useMemo<ParentalControlsContextType>(
    () => ({
      state,
      isEnabled,
      isKidsMode,
      isBlocked,
      isNavVisible,
      hasPassword,
      verifyPassword,
      setPassword,
      changePassword,
      setEnabled,
      setKidsMode,
      setSectionBlocked,
      setBlockedLiveCategories,
      setBlockedMovieCategories,
      setBlockedSeriesCategories,
      setLiveChannelBlocked,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state]
  );

  return (
    <ParentalControlsContext.Provider value={value}>
      {children}
    </ParentalControlsContext.Provider>
  );
};

export function useParentalControls(): ParentalControlsContextType {
  const ctx = useContext(ParentalControlsContext);
  if (!ctx) throw new Error('useParentalControls must be used inside ParentalControlsProvider');
  return ctx;
}
